/**
 * BulkEmail Component
 *
 * Bulk email sending interface for beta applications with:
 * - Tabbed interface by email type (Approval/Rejection/Waitlist)
 * - Applicant selection with "eligible" vs "already sent" lists
 * - Progress tracking for bulk operations
 * - Integration with existing email templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Users, Mail, CheckCircle, XCircle, Clock, AlertCircle, Send, RefreshCcw } from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  ButtonGroup,
  Chip,
  Spinner,
  Alert,
  Input,
  Progress,
  Listbox,
  ListboxItem,
  Skeleton,
  useToastNotification
} from '@enfomentis/ui';

import { betaApplicationService } from '@/services/BetaApplicationService';
import { communicationService } from '@/services/CommunicationService';
import { emailService } from '@/services/EmailService';
import { emailTemplateService } from '@/services/EmailTemplateService';
import { invitationTokenService } from '@/services/InvitationTokenService';
import { useAuth } from '@/hooks/useAuth';
import type { BetaApplication } from '@/types/supabase/BetaApplication';
import type { Communication } from '@/types/supabase/Communication';
import type { InvitationToken } from '@/types/supabase/InvitationToken';
import type { EmailTemplateType } from '@/types/email';

/**
 * Bulk operation status for tracking progress
 */
interface BulkOperationStatus {
  isRunning: boolean;
  progress: number;
  total: number;
  completed: number;
  failed: number;
  currentApplicant?: string | undefined;
  errors: Array<{ applicantId: string; applicantName: string; error: string }>;
}

/**
 * Applicant with email status information
 */
interface ApplicantEmailStatus extends BetaApplication {
  hasReceivedEmail: boolean;
  hasReceivedInvitation: boolean;
  lastEmailSent?: Date | undefined;
  emailHistory: Communication[];
  invitationToken?: InvitationToken | null;
}

/**
 * Bulk Email Component
 *
 * Features:
 * - Email type tabs (Approval, Rejection, Waitlist)
 * - Applicant lists with eligibility status
 * - Bulk selection controls
 * - Progress tracking for bulk operations
 * - Real-time status updates
 * - Error handling and retry capability
 *
 * Usage:
 * ```tsx
 * <BulkEmail />
 * ```
 */
export const BulkEmail: React.FC = () => {
  // Auth context for admin username
  const { user } = useAuth();
  const adminUsername = user?.email || 'Unknown Admin';

  // State management
  const [selectedEmailType, setSelectedEmailType] = useState<EmailTemplateType>('approval');
  const [applicants, setApplicants] = useState<ApplicantEmailStatus[]>([]);
  const [selectedApplicants, setSelectedApplicants] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bulkStatus, setBulkStatus] = useState<BulkOperationStatus>({
    isRunning: false,
    progress: 0,
    total: 0,
    completed: 0,
    failed: 0,
    errors: []
  });

  // Toast notifications
  const { showToast } = useToastNotification();

  // Load applicants and their email status
  const loadApplicants = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Load all beta applications
      const allApplications = await betaApplicationService.getAll();

      // Map applicants with email status by checking communications
      const applicantsWithStatus: ApplicantEmailStatus[] = await Promise.all(
        allApplications.map(async (app) => {
          try {
            // Get all communications for this applicant
            const appCommunications = await communicationService.getByEntity('beta_application', app.id);

            // Check if applicant has received the current email type
            const hasReceivedCurrentType = await communicationService.hasEmailBeenSent(
              'beta_application',
              app.id,
              selectedEmailType
            );

            const lastEmail = appCommunications
              .filter(comm => comm.status === 'sent')
              .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0];

            // For invitation and activation-reminder email types, load invitation token
            let invitationToken: InvitationToken | null = null;
            let hasReceivedInvitation = false;
            if (selectedEmailType === 'invitation' || selectedEmailType === 'activation-reminder') {
              try {
                invitationToken = await invitationTokenService.getActiveTokenForApplication(app.id);
                // Check if they've received an invitation email specifically (for activation-reminder eligibility)
                hasReceivedInvitation = await communicationService.hasEmailBeenSent(
                  'beta_application',
                  app.id,
                  'invitation'
                );
              } catch (err) {
                console.warn(`Failed to load invitation token for ${app.email}:`, err);
              }
            }

            return {
              ...app,
              hasReceivedEmail: hasReceivedCurrentType,
              hasReceivedInvitation, // Add this for activation-reminder logic
              lastEmailSent: lastEmail?.sentAt ? new Date(lastEmail.sentAt) : undefined,
              emailHistory: appCommunications,
              invitationToken
            };
          } catch (err) {
            console.error(`Failed to load communications for ${app.email}:`, err);
            // Return applicant with no email history if communication loading fails
            return {
              ...app,
              hasReceivedEmail: false,
              hasReceivedInvitation: false,
              lastEmailSent: undefined,
              emailHistory: [],
              invitationToken: null
            };
          }
        })
      );

      setApplicants(applicantsWithStatus);
    } catch (err) {
      console.error('Failed to load applicants:', err);
      setError('Failed to load applicants. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEmailType]);

  // Load data on component mount and when email type changes
  useEffect(() => {
    loadApplicants();
  }, [loadApplicants]);

  // Clear bulk progress when switching email types
  useEffect(() => {
    if (bulkStatus.total > 0 && !bulkStatus.isRunning) {
      setBulkStatus({
        isRunning: false,
        progress: 0,
        total: 0,
        completed: 0,
        failed: 0,
        errors: []
      });
    }
  }, [selectedEmailType, bulkStatus.total, bulkStatus.isRunning]);

  // Handle clearing bulk progress
  const handleClearProgress = (): void => {
    setBulkStatus({
      isRunning: false,
      progress: 0,
      total: 0,
      completed: 0,
      failed: 0,
      errors: []
    });
  };

  // Filter applicants based on search term and current email type
  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = !searchTerm ||
      applicant.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by email type - only show applicants that match the selected email type
    const matchesEmailType = (() => {
      switch (selectedEmailType) {
        case 'approval':
          return applicant.status === 'approved';
        case 'rejection':
          return applicant.status === 'rejected';
        case 'waitlist':
          return applicant.status === 'waitlisted';
        case 'invitation':
        case 'activation-reminder':
          // For invitation/activation-reminder, only show approved applicants
          return applicant.status === 'approved';
        default:
          return true;
      }
    })();

    return matchesSearch && matchesEmailType;
  });

  // Separate eligible and already sent applicants based on email type
  const eligibleApplicants = filteredApplicants.filter(app => {
    if (selectedEmailType === 'invitation') {
      // Eligible for invitation: approved applications without invitation email sent yet
      return !app.hasReceivedEmail;
    } else if (selectedEmailType === 'activation-reminder') {
      // Eligible for activation reminder: invitation email was sent AND token not used yet
      // This includes both expired and non-expired tokens (user may have lost email, etc.)
      if (!app.invitationToken) return false;

      const isTokenUnused = !app.invitationToken.usedAt;
      const hasReceivedInvitation = app.hasReceivedInvitation;

      return hasReceivedInvitation && isTokenUnused;
    } else {
      // For other email types, eligible if email hasn't been sent
      return !app.hasReceivedEmail;
    }
  });

  const alreadySentApplicants = filteredApplicants.filter(app => {
    if (selectedEmailType === 'invitation') {
      return app.hasReceivedEmail;
    } else if (selectedEmailType === 'activation-reminder') {
      // Show in "already sent" if token was used (they activated)
      return app.invitationToken?.usedAt !== null && app.invitationToken?.usedAt !== undefined;
    } else {
      return app.hasReceivedEmail;
    }
  });

  // Handle select all eligible
  const handleSelectAllEligible = (): void => {
    setSelectedApplicants(new Set(eligibleApplicants.map(app => app.id)));
  };

  // Handle clear selection
  const handleClearSelection = (): void => {
    setSelectedApplicants(new Set());
  };

  // Handle bulk email send
  const handleBulkSend = async (): Promise<void> => {
    const selectedApplicantsList = applicants.filter(app => selectedApplicants.has(app.id));

    if (selectedApplicantsList.length === 0) {
      showToast({
        color: 'warning',
        title: 'No Selection',
        description: 'Please select at least one applicant to send emails.'
      });
      return;
    }

    // Verify admin user is authenticated
    if (!adminUsername || adminUsername === 'Unknown Admin') {
      showToast({
        color: 'danger',
        title: 'Authentication Error',
        description: 'Admin user not authenticated. Please log in again.'
      });
      return;
    }

    setBulkStatus({
      isRunning: true,
      progress: 0,
      total: selectedApplicantsList.length,
      completed: 0,
      failed: 0,
      errors: []
    });

    let completedCount = 0;
    let failedCount = 0;
    const errors: Array<{ applicantId: string; applicantName: string; error: string }> = [];

    try {
      // Get the email template for this email type
      const emailTemplate = await emailTemplateService.getByType(selectedEmailType);
      if (!emailTemplate) {
        throw new Error(`Email template not found for type: ${selectedEmailType}`);
      }

      // Process each selected applicant
      for (let i = 0; i < selectedApplicantsList.length; i++) {
        const applicant = selectedApplicantsList[i];
        if (!applicant) continue;

        setBulkStatus(prev => ({
          ...prev,
          currentApplicant: applicant.fullName,
          progress: ((i + 1) / selectedApplicantsList.length) * 100
        }));

        try {
          // Real email sending using EmailService with Resend
          if (selectedEmailType === 'invitation' || selectedEmailType === 'activation-reminder') {
            // For invitation/activation-reminder, generate or get invitation token
            let tokenData = applicant.invitationToken;

            if (!tokenData || (selectedEmailType === 'activation-reminder' && new Date(tokenData.expiresAt) < new Date())) {
              // Create new token if none exists or if expired (for activation reminders)
              tokenData = await invitationTokenService.createFromBetaApplication(applicant, 72);
            } else if (selectedEmailType === 'activation-reminder' && tokenData) {
              // For activation reminders with existing valid tokens, extend the expiration
              // This gives the user a fresh 72 hours from the reminder email
              const extended = await invitationTokenService.extendExpiration(tokenData.id, 72);
              if (extended) {
                tokenData = extended;
              }
            }

            if (!tokenData) {
              console.error(`Failed to create/extend token for ${applicant.email}`);
              continue;
            }

            const invitationVariables = {
              recipient_name: applicant.fullName,
              invitation_link: `${import.meta.env['VITE_APP_URL'] || window.location.origin}/activate/${tokenData.token}`,
              invitation_token: tokenData.token,
              expires_at: new Date(tokenData.expiresAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }),
              sent_by: adminUsername,
              sent_date: new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            };

            // Render template with variables
            let renderedContent = emailTemplate.content;
            Object.entries(invitationVariables).forEach(([key, value]) => {
              renderedContent = renderedContent.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
            });

            // Send email via Resend
            // Send email via Edge Function (automatically logs to communications table)
            const emailResult = await emailService.sendCustomEmail({
              to: applicant.email,
              subject: emailTemplate.subject,
              content: renderedContent,
              template_type: selectedEmailType,
              application_id: applicant.id,
              sent_by: adminUsername
            });

            if (emailResult.success) {
              console.log(`✅ Sent ${selectedEmailType} email to: ${applicant.email} via Resend`);
              completedCount++;
            } else {
              const errorMessage = emailResult.error || 'Email delivery failed';
              console.log(`❌ Failed to send ${selectedEmailType} email to: ${applicant.email}`);
              failedCount++;
              errors.push({
                applicantId: applicant.id,
                applicantName: applicant.fullName,
                error: errorMessage
              });
            }
          } else {
            // For other email types (approval, rejection, waitlist), use standard variables
            const emailVariables = {
              applicant_name: applicant.fullName,
              application_date: new Date(applicant.createdAt).toLocaleDateString(),
              decision_date: new Date().toLocaleDateString(),
              admin_name: adminUsername
            };

            // Render template with variables
            let renderedContent = emailTemplate.content;
            Object.entries(emailVariables).forEach(([key, value]) => {
              renderedContent = renderedContent.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
            });

            // Send email via Resend
            const emailResult = await emailService.sendCustomEmail({
              to: applicant.email,
              subject: emailTemplate.subject,
              content: renderedContent,
              template_type: selectedEmailType,
              application_id: applicant.id,
              sent_by: adminUsername
            });

            if (emailResult.success) {
              console.log(`✅ Sent ${selectedEmailType} email to: ${applicant.email} via Resend`);
              completedCount++;
            } else {
              const errorMessage = emailResult.error || 'Email delivery failed';
              console.log(`❌ Failed to send ${selectedEmailType} email to: ${applicant.email}`);
              failedCount++;
              errors.push({
                applicantId: applicant.id,
                applicantName: applicant.fullName,
                error: errorMessage
              });
            }
          }

          setBulkStatus(prev => ({
            ...prev,
            completed: completedCount,
            failed: failedCount,
            errors
          }));

        } catch (err) {
          console.error(`Failed to process email for ${applicant.fullName}:`, err);
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';

          try {
            // Try to record the failure in communications table
            await communicationService.recordEmailFailed(
              'beta_application',
              applicant.id,
              applicant.email,
              emailTemplate,
              {
                applicant_name: applicant.fullName,
                application_date: new Date(applicant.createdAt).toLocaleDateString(),
                decision_date: new Date().toLocaleDateString(),
                admin_name: adminUsername
              },
              adminUsername,
              errorMessage
            );
          } catch (recordError) {
            console.error('Failed to record email failure:', recordError);
          }

          failedCount++;
          errors.push({
            applicantId: applicant.id,
            applicantName: applicant.fullName,
            error: errorMessage
          });

          setBulkStatus(prev => ({
            ...prev,
            failed: failedCount,
            errors
          }));
        }
      }

      // Success notification with local counts
      showToast({
        color: completedCount > 0 ? 'success' : 'warning',
        title: 'Bulk Send Complete',
        description: `Sent ${completedCount} emails successfully. ${failedCount} failed.`
      });

      // Reload applicants to update status
      await loadApplicants();
      setSelectedApplicants(new Set());

    } catch (err) {
      console.error('Bulk send operation failed:', err);
      showToast({
        color: 'danger',
        title: 'Bulk Send Failed',
        description: err instanceof Error ? err.message : 'The bulk email operation encountered an error. Please try again.'
      });
    } finally {
      setBulkStatus(prev => ({
        ...prev,
        isRunning: false,
        currentApplicant: undefined
      }));
    }
  };

  // Render applicant list using shared Listbox component
  const renderApplicantList = (applicantsList: ApplicantEmailStatus[], title: string, color: 'success' | 'default' | 'warning'): React.ReactNode => {
    if (applicantsList.length === 0) {
      const isEligibleList = title.includes('Eligible');
      const emptyMessage = isEligibleList
        ? `No ${selectedEmailType} applicants eligible for email notifications`
        : `No ${selectedEmailType} applicants have received email notifications yet`;

      return (
        <Card shadow="sm">
          <CardBody className="text-center py-8">
            <div className="flex flex-col items-center gap-2">
              <Users size={32} className="text-default-300" />
              <p className="text-sm font-medium text-text-secondary">{emptyMessage}</p>
              {isEligibleList && (
                <p className="text-xs text-default-400 max-w-xs">
                  {selectedEmailType === 'approval' && 'Only approved applicants can receive approval emails'}
                  {selectedEmailType === 'rejection' && 'Only rejected applicants can receive rejection emails'}
                  {selectedEmailType === 'waitlist' && 'Only waitlisted applicants can receive waitlist emails'}
                  {selectedEmailType === 'invitation' && 'Only approved applicants who have not yet received an invitation email can be sent invitations'}
                  {selectedEmailType === 'activation-reminder' && 'Only approved applicants who received an invitation but have not activated yet can receive activation reminders'}
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      );
    }

    const isEligibleList = title.includes('Eligible');

    return (
      <Card shadow="sm">
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-text-primary">{title}</h4>
              <Chip color={color} variant="flat" size="sm">
                {applicantsList.length}
              </Chip>
            </div>
            {isEligibleList && applicantsList.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  onPress={handleSelectAllEligible}
                  isDisabled={bulkStatus.isRunning}
                >
                  Select All
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  color="secondary"
                  onPress={handleClearSelection}
                  isDisabled={bulkStatus.isRunning}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody className="max-h-64 overflow-y-auto">
          {isEligibleList ? (
            <Listbox
              aria-label={title}
              selectionMode="multiple"
              selectedKeys={selectedApplicants}
              onSelectionChange={(keys) => {
                setSelectedApplicants(new Set(Array.from(keys) as string[]));
              }}
              className="w-full [&_[data-selected=true]]:!bg-transparent [&_[aria-selected=true]]:!bg-transparent [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
            >
              {applicantsList.map(applicant => (
                <ListboxItem
                  key={applicant.id}
                  textValue={`${applicant.fullName} ${applicant.email}`}
                  isDisabled={bulkStatus.isRunning}
                  startContent={
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                      {applicant.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                  }
                >
                  <div className="flex justify-between items-center w-full">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-text-primary">{applicant.fullName}</span>
                      <span className="text-xs text-text-secondary">{applicant.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip
                        color={
                          applicant.status === 'approved' ? 'success' :
                          applicant.status === 'rejected' ? 'danger' :
                          applicant.status === 'waitlisted' ? 'warning' : 'default'
                        }
                        variant="flat"
                        size="sm"
                      >
                        {applicant.status}
                      </Chip>
                      {applicant.lastEmailSent && (
                        <span className="text-xs text-text-secondary">
                          {applicant.lastEmailSent.toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </ListboxItem>
              ))}
            </Listbox>
          ) : (
            // Read-only list for "Already Sent" applicants
            <div className="space-y-2">
              {applicantsList.map(applicant => (
                <div key={applicant.id} className="flex items-center justify-between p-2 rounded-md bg-default-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-default-300 text-white flex items-center justify-center text-xs font-semibold">
                      {applicant.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{applicant.fullName}</p>
                      <p className="text-xs text-text-secondary">{applicant.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip
                      color={
                        applicant.status === 'approved' ? 'success' :
                        applicant.status === 'rejected' ? 'danger' :
                        applicant.status === 'waitlisted' ? 'warning' : 'default'
                      }
                      variant="flat"
                      size="sm"
                    >
                      {applicant.status}
                    </Chip>
                    {applicant.lastEmailSent && (
                      <span className="text-xs text-text-secondary">
                        {applicant.lastEmailSent.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    );
  };


  if (isLoading) {
    return (
      <Card shadow="none" radius="none" fullWidth className="h-full">
        {/* Header Skeleton */}
        <CardHeader className="flex-shrink-0 border-b border-default-200">
          <div className="w-full space-y-4">
            {/* ButtonGroup Skeleton */}
            <Skeleton className="w-full h-12 rounded" />
            {/* Search Skeleton */}
            <Skeleton className="w-full h-10 rounded" />
          </div>
        </CardHeader>

        {/* Content Skeleton */}
        <CardBody className="space-y-6 overflow-y-auto">
          {/* Applicant Lists Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Card shadow="sm">
                <CardHeader>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-32 h-5 rounded" />
                      <Skeleton className="w-8 h-5 rounded-full" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="w-20 h-8 rounded" />
                      <Skeleton className="w-16 h-8 rounded" />
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-2">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="w-32 h-4 rounded" />
                        <Skeleton className="w-48 h-3 rounded" />
                      </div>
                      <Skeleton className="w-16 h-6 rounded-full" />
                    </div>
                  ))}
                </CardBody>
              </Card>
            </div>

            <div className="space-y-4">
              <Card shadow="sm">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-28 h-5 rounded" />
                    <Skeleton className="w-6 h-5 rounded-full" />
                  </div>
                </CardHeader>
                <CardBody className="space-y-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-2">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="w-28 h-4 rounded" />
                        <Skeleton className="w-44 h-3 rounded" />
                      </div>
                      <Skeleton className="w-14 h-6 rounded-full" />
                    </div>
                  ))}
                </CardBody>
              </Card>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Alert color="danger" variant="flat" className="mb-4">
          <AlertCircle size={16} />
          {error}
        </Alert>
        <Button
          color="primary"
          variant="flat"
          onPress={loadApplicants}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Card shadow="none" radius="none" fullWidth className='h-full flex flex-col'>
      {/* Header with Email Type Selection */}
      <CardHeader className="flex-shrink-0 flex border-b border-default-200">
        <div className="w-full space-y-4">
          <ButtonGroup
            options={[
              { label: <span className="hidden sm:inline">Approval</span>, value: 'approval', icon: CheckCircle },
              { label: <span className="hidden sm:inline">Rejection</span>, value: 'rejection', icon: XCircle },
              { label: <span className="hidden sm:inline">Waitlist</span>, value: 'waitlist', icon: Clock },
              { label: <span className="hidden sm:inline">Confirmation</span>, value: 'confirmation', icon: Mail },
              { label: <span className="hidden sm:inline">Invitation</span>, value: 'invitation', icon: Send },
              { label: <span className="hidden sm:inline">Activation Reminder</span>, value: 'activation-reminder', icon: RefreshCcw }
            ]}
            value={selectedEmailType}
            onChange={(value) => {
              // Prevent white screen by ensuring we always have a valid value
              if (value && typeof value === 'string' && ['approval', 'rejection', 'waitlist', 'confirmation', 'invitation', 'activation-reminder'].includes(value)) {
                setSelectedEmailType(value as EmailTemplateType);
                setSelectedApplicants(new Set()); // Clear selection when switching
              }
            }}
            variant="light"
            color="default"
            size="md"
            radius="none"
            fullWidth
            underlined
            selectedColor="primary"
          />

          {/* Search */}
          <Input
            placeholder={`Search ${selectedEmailType} applicants by name or email...`}
            value={searchTerm}
            onValueChange={setSearchTerm}
            startContent={<Users size={16} className="text-text-secondary" />}
            isClearable
          />
        </div>
      </CardHeader>

      {/* Scrollable Content */}
      <CardBody className="space-y-6 flex-1 overflow-y-auto">
        {/* Applicant Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {renderApplicantList(
              eligibleApplicants,
              `Eligible for ${selectedEmailType.charAt(0).toUpperCase() + selectedEmailType.slice(1)} Email`,
              'success'
            )}
          </div>

          <div className="space-y-4">
            {renderApplicantList(
              alreadySentApplicants,
              'Already Received Email',
              'default'
            )}
          </div>
        </div>
      </CardBody>

      {/* Footer with Send Controls and Bulk Progress */}
      {(selectedApplicants.size > 0 || bulkStatus.total > 0) && (
        <CardFooter className="flex-shrink-0 border-t border-default-200">
          <div className="w-full space-y-3">
            {/* Bulk Progress Section */}
            {bulkStatus.total > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-text-primary">
                      {bulkStatus.isRunning ? 'Sending Emails...' : 'Bulk Operation Complete'}
                    </h4>
                    {bulkStatus.isRunning && <Spinner size="sm" color="primary" />}
                  </div>
                  {!bulkStatus.isRunning && (
                    <Button
                      size="sm"
                      variant="light"
                      color="default"
                      onPress={handleClearProgress}
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <Progress
                  value={bulkStatus.progress}
                  color="primary"
                  size="sm"
                  showValueLabel
                  aria-label="Bulk email sending progress"
                />

                <div className="flex items-center gap-4 text-xs text-text-secondary">
                  <span>Total: {bulkStatus.total}</span>
                  <span className="text-success">Completed: {bulkStatus.completed}</span>
                  {bulkStatus.failed > 0 && (
                    <span className="text-danger">Failed: {bulkStatus.failed}</span>
                  )}
                  {bulkStatus.isRunning && bulkStatus.currentApplicant && (
                    <span className="text-primary">Sending to: {bulkStatus.currentApplicant}</span>
                  )}
                </div>
              </div>
            )}

            {/* Selection and Send Controls */}
            {selectedApplicants.size > 0 && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {selectedApplicants.size} applicant{selectedApplicants.size !== 1 ? 's' : ''} selected
                  </p>
                  <p className="text-xs text-text-secondary">
                    Ready to send {selectedEmailType} emails
                  </p>
                </div>
                <Button
                  color="primary"
                  variant="solid"
                  startContent={<Mail size={16} />}
                  onPress={handleBulkSend}
                  isLoading={bulkStatus.isRunning}
                  isDisabled={bulkStatus.isRunning}
                >
                  Send {selectedApplicants.size} Email{selectedApplicants.size !== 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};